import { useState } from 'react';
import BluffInput from './BluffInput.jsx';
import formatClipTitle from '../../../utils/formatClipTitle.js';
import '../../../styles/theme.css';

export function NameThatNoisePlayer({
  phase = 'bluffing',
  modeState = {},
  playerId = '',
  onSubmitBluff = () => {},
  onSubmitVote = () => {},
  hasSubmitted = false
}) {
  const [selectedVoteId, setSelectedVoteId] = useState(null);
  const [voteSubmitted, setVoteSubmitted] = useState(hasSubmitted);
  const [submittedBluff, setSubmittedBluff] = useState('');

  const options = modeState?.options || [];
  const roundResults = modeState?.roundResults || null;

  const handleBluffSubmit = (bluff) => {
    const text = typeof bluff === 'string' ? bluff : (bluff?.bluff || bluff?.text || '');
    setSubmittedBluff(text);
    onSubmitBluff(text);
  };

  const handleVote = (optionId, authorId) => {
    if (authorId === playerId) return; // Prevent self-vote locally as well
    if (voteSubmitted) return;

    setSelectedVoteId(optionId);
    setVoteSubmitted(true);
    onSubmitVote({ voteOptionId: optionId, vote: optionId });
  };

  return (
    <div
      data-testid="namethatnoise-player"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        width: '100%',
        boxSizing: 'border-box',
        color: 'var(--sd-ink, #EDEFF2)',
        fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
      }}
    >
      {/* PHASE 1: BLUFFING */}
      {(phase === 'bluffing' || phase === 'answering') && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div
            style={{
              textAlign: 'center',
              padding: '24px 20px',
              background: 'var(--sd-panel, #0D1015)',
              border: '1px solid var(--sd-line, #1A1F25)',
              borderRadius: '16px',
            }}
          >
            <span style={{ fontSize: '2.5rem' }}>🎧</span>
            <h2 style={{
              fontSize: '1.4rem',
              color: 'var(--sd-ink, #EDEFF2)',
              margin: '8px 0 4px 0',
              fontWeight: 800
            }}>
              Listen to the Host Speaker!
            </h2>
            <p style={{
              color: 'var(--sd-muted, #79838D)',
              fontSize: '0.95rem',
              margin: 0,
              lineHeight: 1.5,
            }}>
              The mystery sound is playing on the TV. Fool other players by inventing a fake label!
            </p>
          </div>

          <BluffInput
            onSubmitBluff={handleBluffSubmit}
            disabled={hasSubmitted}
          />
        </div>
      )}

      {/* PHASE 2: VOTING */}
      {(phase === 'voting' || phase === 'vote') && (
        <div
          data-testid="voting-card"
          style={{
            padding: '24px 20px',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '16px',
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <span style={{
              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              fontSize: '11px',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--sd-teal, #3ACFCF)',
              fontWeight: 600,
            }}>
              Spot the Truth
            </span>
            <h2 style={{
              fontSize: '1.4rem',
              color: 'var(--sd-ink, #EDEFF2)',
              margin: '6px 0 4px 0',
              fontWeight: 800
            }}>
              Which label is REAL?
            </h2>
            <p style={{
              color: 'var(--sd-muted, #79838D)',
              fontSize: '0.9rem',
              margin: 0,
              lineHeight: 1.4,
            }}>
              Points for picking truth & points for fooling players with your bluff!
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {options.map((option) => {
              const isOwnBluff = option.authorId === playerId || (submittedBluff && option.text.trim().toLowerCase() === submittedBluff.trim().toLowerCase());
              const isSelected = selectedVoteId === option.id;

              return (
                <button
                  key={option.id}
                  type="button"
                  data-testid={`vote-option-${option.id}`}
                  disabled={isOwnBluff || voteSubmitted}
                  onClick={() => handleVote(option.id, option.authorId)}
                  style={{
                    padding: '16px 20px',
                    borderRadius: '12px',
                    border: isSelected
                      ? '1px solid var(--sd-teal, #3ACFCF)'
                      : isOwnBluff
                      ? '1px solid var(--sd-line, #1A1F25)'
                      : '1px solid var(--sd-line-2, #1C2229)',
                    background: isSelected
                      ? 'var(--sd-teal, #3ACFCF)'
                      : isOwnBluff
                      ? 'var(--sd-panel-2, #0E1116)'
                      : 'var(--sd-panel-2, #0E1116)',
                    color: isSelected
                      ? 'var(--sd-ground, #08090B)'
                      : isOwnBluff
                      ? 'var(--sd-dim, #6E7982)'
                      : 'var(--sd-ink, #EDEFF2)',
                    cursor: isOwnBluff || voteSubmitted ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    textAlign: 'left',
                    fontSize: '1rem',
                    fontWeight: 700,
                    minHeight: '52px',
                    transition: 'all 0.15s ease',
                    fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                  }}
                >
                  <span>{option.text}</span>
                  {isOwnBluff && (
                    <span
                      data-testid="self-bluff-badge"
                      style={{
                        fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                        fontSize: '10px',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: 'var(--sd-dim, #6E7982)',
                        padding: '2px 8px',
                        borderRadius: '6px',
                        background: 'var(--sd-ground, #08090B)',
                      }}
                    >
                      Your Bluff
                    </span>
                  )}
                  {isSelected && <span>✓</span>}
                </button>
              );
            })}
          </div>

          {voteSubmitted && (
            <div
              data-testid="vote-submitted-banner"
              style={{
                marginTop: '16px',
                padding: '12px 16px',
                borderRadius: '12px',
                background: 'rgba(58, 207, 207, 0.10)',
                border: '1px solid var(--sd-teal, #3ACFCF)',
                color: 'var(--sd-teal, #3ACFCF)',
                textAlign: 'center',
                fontWeight: 700,
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                fontSize: '12px',
                letterSpacing: '0.06em',
              }}
            >
              🔒 Vote Registered! Waiting for reveal on TV...
            </div>
          )}
        </div>
      )}

      {/* PHASE 3: RESULTS */}
      {(phase === 'results' || phase === 'reveal') && (
        <div
          data-testid="player-results-card"
          style={{
            padding: '24px 20px',
            textAlign: 'center',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '16px',
          }}
        >
          <h2 style={{
            fontSize: '1.4rem',
            color: 'var(--sd-amber, #E0A458)',
            margin: '0 0 16px 0',
            fontWeight: 800,
            fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            letterSpacing: '0.04em',
          }}>
            🎉 Round Results
          </h2>

          {roundResults?.mysteryClip && (
            <div style={{
              background: 'var(--sd-panel-2, #0E1116)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '16px',
              border: '1px solid var(--sd-line-2, #1C2229)'
            }}>
              <div style={{
                fontSize: '10.5px',
                color: 'var(--sd-dim, #6E7982)',
                textTransform: 'uppercase',
                fontWeight: 600,
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                letterSpacing: '0.14em',
              }}>
                Real Sound Label
              </div>
              <div style={{ fontSize: '1.3rem', color: 'var(--sd-sage, #8FC7A0)', fontWeight: 800, marginTop: '4px', wordBreak: 'break-word' }}>
                {formatClipTitle(roundResults.mysteryClip)}
              </div>
            </div>
          )}

          {roundResults?.roundScores?.[playerId] && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', color: 'var(--sd-ink-2, #C6CDD4)' }}>
                <span>Picked Truth:</span>
                <strong style={{ color: roundResults.roundScores[playerId].pickedTruth ? 'var(--sd-sage, #8FC7A0)' : 'var(--sd-rose, #E4738A)' }}>
                  {roundResults.roundScores[playerId].pickedTruth ? '+1000 pts' : '0 pts'}
                </strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', color: 'var(--sd-ink-2, #C6CDD4)' }}>
                <span>Fooled Players ({roundResults.roundScores[playerId].fooledCount}):</span>
                <strong style={{ color: 'var(--sd-teal, #3ACFCF)' }}>
                  +{roundResults.roundScores[playerId].fooledPoints} pts
                </strong>
              </div>
              <div style={{
                borderTop: '1px solid var(--sd-line, #1A1F25)',
                paddingTop: '12px',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '1.2rem',
                fontWeight: 800,
                color: 'var(--sd-amber, #E0A458)',
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              }}>
                <span>Total Round:</span>
                <span>+{roundResults.roundScores[playerId].total} pts</span>
              </div>
            </div>
          )}

          {/* All Submissions & Votes Breakdown on Phone */}
          {roundResults?.options && (
            <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '16px', borderTop: '1px solid var(--sd-line, #1A1F25)', paddingTop: '16px' }}>
              <div style={{
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                fontSize: '11px',
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'var(--sd-muted, #79838D)',
                marginBottom: '4px',
              }}>
                Submissions Breakdown
              </div>
              {roundResults.options.map((opt) => {
                const voterIds = Object.entries(roundResults.votes || {})
                  .filter(([, vId]) => vId === opt.id)
                  .map(([vId]) => vId);

                return (
                  <div
                    key={opt.id}
                    style={{
                      background: opt.isTruth ? 'rgba(143, 199, 160, 0.12)' : 'var(--sd-panel-2, #0E1116)',
                      border: `1px solid ${opt.isTruth ? 'rgba(143, 199, 160, 0.4)' : 'var(--sd-line-2, #1C2229)'}`,
                      borderRadius: '12px',
                      padding: '12px 14px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: 700, fontSize: '0.95rem', color: opt.isTruth ? 'var(--sd-sage, #8FC7A0)' : 'var(--sd-ink, #EDEFF2)' }}>
                        {opt.text}
                      </span>
                      <span style={{
                        fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                        fontSize: '10px',
                        textTransform: 'uppercase',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        background: opt.isTruth ? 'rgba(143, 199, 160, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                        color: opt.isTruth ? 'var(--sd-sage, #8FC7A0)' : 'var(--sd-dim, #6E7982)',
                      }}>
                        {opt.isTruth ? 'TRUTH' : (opt.authorId === playerId ? 'Your Bluff' : 'Bluff')}
                      </span>
                    </div>
                    {voterIds.length > 0 && (
                      <div style={{ fontSize: '11px', color: 'var(--sd-teal, #3ACFCF)', fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace" }}>
                        Voted by {voterIds.length} player{voterIds.length > 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NameThatNoisePlayer;
